import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  conectarHolded,
  getHoldedEstado,
  desconectarHolded,
  sincronizarHolded,
  getEjercicios,
  getHoldedApuntes,
} from "@/lib/api";
import { notifyDataChanged } from "@/lib/events";
import { formatEur } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Search,
  BookOpen,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Conexión con Holded (por empresa). El diario de Holded se convierte en el
// Sumas y Saldos de siempre — el análisis y la proyección NO cambian; solo el
// origen del dato. Los apuntes quedan guardados para el drilldown de abajo.
// ---------------------------------------------------------------------------

export default function ConectarHoldedPage() {
  const { empresaId } = useParams();
  const navigate = useNavigate();

  const [estado, setEstado] = useState(null); // null = cargando
  const [apiKey, setApiKey] = useState("");
  const [anios, setAnios] = useState("5");
  const [conectando, setConectando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [confirmarDesconexion, setConfirmarDesconexion] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [resultado, setResultado] = useState(null); // resumen de la última sync

  // Drilldown de apuntes
  const [ejercicios, setEjercicios] = useState([]);
  const [ejercicioSel, setEjercicioSel] = useState("");
  const [cuentaFiltro, setCuentaFiltro] = useState("");
  const [apuntes, setApuntes] = useState(null);
  const [buscandoApuntes, setBuscandoApuntes] = useState(false);

  const cargarEstado = useCallback(async () => {
    try {
      const { data } = await getHoldedEstado(empresaId);
      setEstado(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar el estado de Holded");
      setEstado({ conectado: false });
    }
  }, [empresaId]);

  const cargarEjercicios = useCallback(async () => {
    try {
      const { data } = await getEjercicios(empresaId);
      const conDatos = (data || [])
        .filter((e) => e.total_cuentas > 0)
        .sort((a, b) => b.anio - a.anio);
      setEjercicios(conDatos);
      if (conDatos.length > 0) setEjercicioSel(conDatos[0].id);
    } catch {
      setEjercicios([]);
    }
  }, [empresaId]);

  useEffect(() => {
    cargarEstado();
    cargarEjercicios();
  }, [cargarEstado, cargarEjercicios]);

  const handleConectar = async () => {
    if (!apiKey.trim()) {
      toast.error("Introduce la API key de Holded");
      return;
    }
    setConectando(true);
    try {
      const { data } = await conectarHolded(empresaId, apiKey.trim(), parseInt(anios) || 5);
      toast.success(
        `Conectado con Holded · plan de cuentas con ${data.cuentas_plan} cuentas`
      );
      setApiKey("");
      await cargarEstado();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "No se pudo conectar con Holded. Revisa la API key."
      );
    } finally {
      setConectando(false);
    }
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    setResultado(null);
    try {
      const { data } = await sincronizarHolded(empresaId);
      setResultado(data);
      toast.success(
        `Sincronizado: ${data.anios_sincronizados?.length || 0} ejercicio(s) actualizados`
      );
      // El S&S de la empresa ha cambiado: refrescar sidebar y ejercicios.
      notifyDataChanged("ejercicios");
      await Promise.all([cargarEstado(), cargarEjercicios()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar con Holded");
    } finally {
      setSincronizando(false);
    }
  };

  const handleDesconectar = async () => {
    setDesconectando(true);
    try {
      await desconectarHolded(empresaId);
      toast.success("Conexión con Holded eliminada (los ejercicios se conservan)");
      setConfirmarDesconexion(false);
      setResultado(null);
      setApuntes(null);
      await cargarEstado();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al desconectar");
    } finally {
      setDesconectando(false);
    }
  };

  const handleBuscarApuntes = async () => {
    if (!ejercicioSel) {
      toast.error("Selecciona un ejercicio");
      return;
    }
    setBuscandoApuntes(true);
    try {
      const { data } = await getHoldedApuntes(ejercicioSel, cuentaFiltro.trim() || null);
      setApuntes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar los apuntes");
    } finally {
      setBuscandoApuntes(false);
    }
  };

  if (estado === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/empresas/${empresaId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">Holded</h1>
          <p className="text-muted-foreground mt-1">
            Conecta la contabilidad de Holded: el diario se convierte en el Sumas y
            Saldos de siempre. El análisis y la proyección no cambian — solo el origen
            del dato, con el detalle de apuntes disponible abajo.
          </p>
        </div>
      </div>

      {/* Estado / Conectar */}
      {!estado.conectado ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Conectar con Holded
            </CardTitle>
            <CardDescription>
              Genera una API key en Holded (Configuración → Desarrolladores → Nueva API
              Key) y pégala aquí. Se guarda cifrada y nunca se muestra.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="holded-key">API key de Holded</Label>
                <Input
                  id="holded-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Pega aquí tu API key"
                  data-testid="holded-api-key"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Años a sincronizar</Label>
                <Select value={anios} onValueChange={setAnios}>
                  <SelectTrigger data-testid="holded-anios">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} año{n !== 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleConectar} disabled={conectando || !apiKey.trim()} data-testid="holded-conectar">
              {conectando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Conectar
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  Conectado a Holded
                </CardTitle>
                <CardDescription>
                  Sincroniza {estado.anios} año{estado.anios !== 1 ? "s" : ""}.
                  {estado.ultima_sincronizacion
                    ? ` Última sincronización: ${new Date(estado.ultima_sincronizacion).toLocaleString("es-ES")}.`
                    : " Aún no se ha sincronizado."}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSincronizar} disabled={sincronizando} data-testid="holded-sincronizar">
                  {sincronizando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {sincronizando ? "Sincronizando…" : "Sincronizar ahora"}
                </Button>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmarDesconexion(true)}
                  data-testid="holded-desconectar"
                >
                  <Unplug className="h-4 w-4 mr-2" />
                  Desconectar
                </Button>
              </div>
            </div>
          </CardHeader>
          {sincronizando && (
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Bajando el diario de Holded y reconstruyendo el Sumas y Saldos… puede
                tardar un poco según los años y el volumen de asientos.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Resultado de la última sincronización */}
      {resultado && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Resultado de la sincronización</CardTitle>
            <CardDescription>
              {resultado.lineas_diario} líneas de diario · {resultado.apuntes_guardados}{" "}
              apuntes guardados para el detalle
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Ejercicio</TableHead>
                  <TableHead className="text-right">Cuentas</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">PyG</TableHead>
                  <TableHead className="text-right">Pendientes</TableHead>
                  <TableHead>Avisos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(resultado.ejercicios || []).map((e) => (
                  <TableRow key={e.anio} className="hover:bg-muted/20">
                    <TableCell className="font-medium">{e.anio}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.stats?.total}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.stats?.balance}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.stats?.pyg}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {e.stats?.pendientes > 0 ? (
                        <Badge variant="outline" className="text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800">
                          {e.stats.pendientes}
                        </Badge>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell>
                      {(e.avisos || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="space-y-0.5">
                          {e.avisos.map((a, i) => (
                            <p key={i} className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1">
                              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                              {a}
                            </p>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="px-4 py-2 text-[11px] text-muted-foreground">
              Las cuentas pendientes se clasifican en la pantalla de Revisión, igual que
              con un Excel importado.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Drilldown de apuntes */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            Detalle de apuntes (diario de Holded)
          </CardTitle>
          <CardDescription>
            Solo consulta: el análisis sigue haciéndose sobre el Sumas y Saldos. Busca
            por ejercicio y prefijo de cuenta (p.ej. 70, 662, 70500000).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={ejercicioSel} onValueChange={setEjercicioSel}>
              <SelectTrigger className="sm:w-[180px]" data-testid="holded-drill-ejercicio">
                <SelectValue placeholder="Ejercicio" />
              </SelectTrigger>
              <SelectContent>
                {ejercicios.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    Ejercicio {e.anio}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={cuentaFiltro}
              onChange={(e) => setCuentaFiltro(e.target.value)}
              placeholder="Prefijo de cuenta (opcional)"
              className="sm:w-[240px] font-mono"
              data-testid="holded-drill-cuenta"
            />
            <Button onClick={handleBuscarApuntes} disabled={buscandoApuntes || !ejercicioSel} variant="outline">
              {buscandoApuntes ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Buscar apuntes
            </Button>
            <Button
              variant="ghost"
              disabled={!ejercicioSel}
              onClick={() => navigate(`/ejercicios/${ejercicioSel}/contabilidad`)}
              data-testid="holded-abrir-libro"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Abrir libro contable
            </Button>
          </div>

          {apuntes && (
            <>
              {apuntes.total === 0 ? (
                <Alert>
                  <AlertDescription>
                    Sin apuntes para ese ejercicio/cuenta. (Los apuntes se guardan al
                    sincronizar; si desconectaste Holded, el detalle se eliminó.)
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="w-[100px]">Fecha</TableHead>
                        <TableHead className="w-[90px]">Asiento</TableHead>
                        <TableHead className="w-[120px]">Cuenta</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead className="text-right w-[120px]">Debe</TableHead>
                        <TableHead className="text-right w-[120px]">Haber</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apuntes.apuntes.map((a, i) => (
                        <TableRow key={i} className="hover:bg-muted/20">
                          <TableCell className="text-xs">{a.fecha}</TableCell>
                          <TableCell className="text-xs tabular-nums">
                            {a.asiento ?? "—"}
                            {a.es_cierre && (
                              <Badge variant="outline" className="ml-1 text-[10px]">cierre</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{a.cuenta}</TableCell>
                          <TableCell className="text-xs max-w-[320px] truncate">{a.concepto || "—"}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {a.debe ? formatEur(a.debe) : "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-xs">
                            {a.haber ? formatEur(a.haber) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {apuntes.truncado && (
                    <p className="px-4 py-2 text-[11px] text-muted-foreground">
                      Mostrando {apuntes.apuntes.length} de {apuntes.total} apuntes —
                      afina el prefijo de cuenta para acotar.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmación de desconexión */}
      <AlertDialog open={confirmarDesconexion} onOpenChange={setConfirmarDesconexion}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desconectar Holded?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán la credencial guardada y el detalle de apuntes. Los
              ejercicios y el Sumas y Saldos importados SE CONSERVAN y puedes seguir
              trabajando con ellos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={desconectando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDesconectar();
              }}
              disabled={desconectando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {desconectando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
